/**
 * LearningTimeTracker — silent "minutes spent learning".
 *
 * While a signed-in learner has a learning page open AND visible, a heartbeat goes to the server every 30 seconds;
 * the server adds the time to that day's total (and ignores heartbeats that come too fast). Nothing is shown and
 * nothing about what the learner did is sent. Parents and teacher-tier roles are not tracked.
 * Renders nothing; mounted once inside the router.
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@context/AuthContext';
import { isParentUser, parentService } from '@services/api/parent.service';

const HEARTBEAT_MS = 30_000;

/** Pages where being on them means learning (not browsing a list, settings or billing). */
const LEARNING_PATHS = [
  '/my-instructor', '/study-material/', '/reading-assessment/', '/handwriting-practice/', '/replay-academy',
  '/ask-question', '/doubt-session', '/join-class', '/voice', '/chat',
  '/knowledge-capsules/', '/language-learning/lessons/', '/language-learning/activity/',
];
const NOT_TRACKED_ROLES = ['teacher', 'special educator', 'school counselor', 'admin', 'super admin', 'school admin', 'mentor'];

export const isLearningPath = (pathname: string): boolean =>
  LEARNING_PATHS.some(p => (p.endsWith('/') ? pathname.startsWith(p) : pathname === p || pathname.startsWith(`${p}/`)));

export const LearningTimeTracker: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const { pathname } = useLocation();
  const roles: string[] = ((user as any)?.roles ?? ((user as any)?.role ? [(user as any).role] : [])).map((r: string) => String(r).toLowerCase());
  const tracked = isAuthenticated && !!user && !isParentUser(user as any) && !roles.some(r => NOT_TRACKED_ROLES.includes(r));
  const learning = tracked && isLearningPath(pathname);

  useEffect(() => {
    if (!learning) return;
    const beat = () => { if (document.visibilityState === 'visible') void parentService.heartbeat(); };
    const timer = setInterval(beat, HEARTBEAT_MS);
    return () => clearInterval(timer);
  }, [learning]);

  return null;
};

export default LearningTimeTracker;
